import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Star, Trash2, Edit2, Save, X, Tag, Copy, Check, CheckSquare, Square, Palette, GitCompare } from 'lucide-react';
import { useColorFavorites } from '../hooks/useColorFavorites';
import { useColorActions } from '../hooks/useColorActions';
import { usePaletteContext } from '../contexts/PaletteContext';
import type { ColorFavoriteRecord } from '../lib/database';
import { useAlert } from '@ascend/alert';

interface ColorFavoritesPanelProps {
  onSelectColor: (hex: string) => void;
  currentHex?: string;
  isFavorited?: boolean;
}

export function ColorFavoritesPanel({ onSelectColor, currentHex, isFavorited }: ColorFavoritesPanelProps) {
  const { favorites, loading, error, removeFavorite, updateFavorite, refresh } = useColorFavorites();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedHexes, setSelectedHexes] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Re-fetch favorites when the active color or its favorited status changes.
  // Note: `refresh` identity is stable (useCallback with [] deps — verified).
  useEffect(() => {
    void refresh();
  }, [currentHex, isFavorited, refresh]);

  const handleEdit = (favorite: ColorFavoriteRecord) => {
    setEditingId(favorite.id!);
    setEditName(favorite.name || '');
    setEditNotes(favorite.notes || '');
  };

  const handleSave = async (id: string) => {
    try {
      await updateFavorite(id, {
        name: editName.trim() || undefined,
        notes: editNotes.trim() || undefined,
      });
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update favorite:', err);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditName('');
    setEditNotes('');
  };

  const { confirm } = useAlert();

  const handleRemove = async (id: string) => {
    const ok = await confirm({ title: 'Remove favorite?', message: 'Remove this color from favorites?', type: 'warning' });
    if (ok) {
      try {
        await removeFavorite(id);
      } catch (err) {
        console.error('Failed to remove favorite:', err);
      }
    }
  };

  const toggleSelect = useCallback((hex: string) => {
    setSelectedHexes(prev => {
      const next = new Set(prev);
      if (next.has(hex)) next.delete(hex);
      else next.add(hex);
      return next;
    });
  }, []);

  const exitSelecting = useCallback(() => {
    setIsSelecting(false);
    setSelectedHexes(new Set());
  }, []);

  const handleCreatePalette = useCallback(async () => {
    if (selectedHexes.size === 0) return;
    const colors = Array.from(selectedHexes);
    navigate('/palette', { state: { colors, from: 'favorites' } });
    exitSelecting();
  }, [selectedHexes, navigate, exitSelecting]);

  const handleCompare = useCallback(() => {
    const colors = Array.from(selectedHexes);
    if (colors.length !== 2) return;
    navigate(`/compare?fg=${colors[0].replace('#', '')}&bg=${colors[1].replace('#', '')}`);
    exitSelecting();
  }, [selectedHexes, navigate, exitSelecting]);

  if (loading) {
    return (
      <div className="fav-section">
        <div className="flex items-center justify-center py-8">
          <Star className="w-5 h-5 animate-spin" style={{ color: 'var(--ink-3)' }} />
          <span className="ml-2" style={{ fontSize: 'var(--t-caption)', color: 'var(--ink-3)' }}>Loading favorites...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fav-section" style={{ borderColor: 'var(--semantic-fail, #dc2626)' }}>
        <div style={{ fontSize: 'var(--t-caption)', color: 'var(--semantic-fail, #dc2626)' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="fav-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5" style={{ color: '#eab308', fill: '#eab308' }} />
          <h3 className="fav-header__title">Favorite Colors</h3>
          <span className="fav-header__count">({favorites.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {favorites.length > 0 && !isSelecting && (
            <button
              onClick={() => setIsSelecting(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 'var(--radius, 6px)',
                background: 'var(--panel, #fff)',
                color: 'var(--ink-2, #6b7280)',
                cursor: 'pointer',
              }}
              title="Select multiple colors"
            >
              <CheckSquare size={13} />
              Select
            </button>
          )}
          {isSelecting && (
            <button
              onClick={exitSelecting}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', fontSize: '12px', fontWeight: 500,
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 'var(--radius, 6px)',
                background: 'var(--panel, #fff)',
                color: 'var(--ink-2, #6b7280)',
                cursor: 'pointer',
              }}
            >
              <X size={13} />
              Cancel
            </button>
          )}
        </div>
      </div>

      {favorites.length === 0 ? (
        <div className="fav-empty">
          <Star className="fav-empty__icon" />
          <p style={{ fontSize: 'var(--t-body)' }}>No favorite colors yet</p>
          <p className="mt-1" style={{ fontSize: 'var(--t-caption)' }}>Click the star icon in the top bar to save colors</p>
          <button
            type="button"
            onClick={() => onSelectColor('dodgerblue')}
            className="fav-empty__cta"
          >
            Try <code style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', padding: '0 var(--s-1)' }}>dodgerblue</code>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {favorites.map((favorite) => (
            <FavoriteItem
              key={favorite.id}
              favorite={favorite}
              isActive={currentHex?.toLowerCase() === favorite.hex.toLowerCase()}
              isEditing={editingId === favorite.id}
              isSelecting={isSelecting}
              isSelected={selectedHexes.has(favorite.hex)}
              editName={editName}
              editNotes={editNotes}
              onEditNameChange={setEditName}
              onEditNotesChange={setEditNotes}
              onUse={() => onSelectColor(favorite.hex)}
              onEdit={() => handleEdit(favorite)}
              onSave={() => handleSave(favorite.id!)}
              onCancel={handleCancel}
              onRemove={() => handleRemove(favorite.id!)}
              onToggleSelect={() => toggleSelect(favorite.hex)}
            />
          ))}
        </div>
      )}

      {/* ── Floating action bar ──────────────────────────────────────── */}
      {isSelecting && selectedHexes.size > 0 && (
        <div style={{
          position: 'sticky', bottom: '16px',
          marginTop: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '10px 16px',
          background: 'var(--ink, #111)', color: '#fff',
          borderRadius: 'var(--radius-xl, 12px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          fontSize: '13px', fontWeight: 500,
        }}>
          <span style={{ opacity: 0.7 }}>{selectedHexes.size} selected</span>
          <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }} />
          <button
            type="button"
            onClick={handleCreatePalette}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: 'var(--radius, 6px)',
              background: 'var(--accent, oklch(0.55 0.15 260))',
              color: '#fff', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: 600,
            }}
          >
            <Palette size={13} />
            Create palette
          </button>
          {selectedHexes.size === 2 && (
            <button
              type="button"
              onClick={handleCompare}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '5px 12px', borderRadius: 'var(--radius, 6px)',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 500,
              }}
            >
              <GitCompare size={13} />
              Compare
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface FavoriteItemProps {
  favorite: ColorFavoriteRecord;
  isActive: boolean;
  isEditing: boolean;
  isSelecting: boolean;
  isSelected: boolean;
  editName: string;
  editNotes: string;
  onEditNameChange: (value: string) => void;
  onEditNotesChange: (value: string) => void;
  onUse: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove: () => void;
  onToggleSelect: () => void;
}

function FavoriteItem({
  favorite,
  isActive,
  isEditing,
  isSelecting,
  isSelected,
  editName,
  editNotes,
  onEditNameChange,
  onEditNotesChange,
  onUse,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onToggleSelect,
}: FavoriteItemProps) {
  const { copyColor, copiedField } = useColorActions();
  const isCopied = copiedField === `fav-${favorite.hex}`;

  return (
    <div
      className={`fav-card group ${isActive ? 'fav-card--active' : ''}`}
      style={{
        outline: isSelected ? '2px solid var(--accent, oklch(0.55 0.15 260))' : undefined,
        outlineOffset: isSelected ? '1px' : undefined,
      }}
      onClick={isSelecting ? onToggleSelect : undefined}
    >
      <div className="flex items-center gap-3 p-3">
        {/* Selection checkbox */}
        {isSelecting && (
          <div style={{ flexShrink: 0 }}>
            {isSelected
              ? <CheckSquare size={18} style={{ color: 'var(--accent, oklch(0.55 0.15 260))' }} />
              : <Square size={18} style={{ color: 'var(--ink-3, #999)' }} />
            }
          </div>
        )}

        <div
          className="fav-swatch"
          style={{ backgroundColor: favorite.hex }}
          title={favorite.hex}
        >
          <div className="w-full h-full" style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'inset 0 0 0 1px rgba(128, 128, 128, 0.15)' }} />
        </div>

        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => onEditNameChange(e.target.value)}
                placeholder="Color name (optional)"
                className="fav-edit-input"
              />
              <input
                type="text"
                value={editNotes}
                onChange={(e) => onEditNotesChange(e.target.value)}
                placeholder="Notes (optional)"
                className="fav-edit-input fav-edit-input--notes"
              />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="fav-card__hex"
                  onClick={(e) => { e.stopPropagation(); copyColor(favorite.hex, `fav-${favorite.hex}`); }}
                  title={isCopied ? 'Copied ✓' : 'Copy hex'}
                  aria-label={isCopied ? 'Copied' : `Copy ${favorite.hex} to clipboard`}
                >
                  {isCopied ? 'Copied ✓' : favorite.hex}
                </button>
                {favorite.name && (
                  <span className="fav-card__name">
                    • {favorite.name}
                  </span>
                )}
              </div>
              {favorite.notes && (
                <p className="fav-card__notes">
                  {favorite.notes}
                </p>
              )}
              {favorite.tags && favorite.tags.length > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Tag className="w-3 h-3" style={{ color: 'var(--ink-3)' }} />
                  {favorite.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="fav-card__tag"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {!isSelecting && (
          <div className="flex items-center gap-1">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={onSave}
                  className="fav-edit-save"
                  title="Save"
                  aria-label="Save changes"
                >
                  <Save className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="fav-edit-cancel"
                  title="Cancel"
                  aria-label="Cancel editing"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onUse}
                  className="fav-card__action fav-card__action--reveal"
                  title="Use as active color"
                  aria-label={`Use ${favorite.hex} as active color`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => copyColor(favorite.hex, `fav-${favorite.hex}`)}
                  className={`fav-card__action fav-card__action--reveal ${isCopied ? 'fav-card__action--copied' : ''}`}
                  title={isCopied ? 'Copied ✓' : 'Copy hex'}
                  aria-label={isCopied ? 'Copied' : `Copy ${favorite.hex}`}
                >
                  {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={onEdit}
                  className="fav-card__action fav-card__action--reveal"
                  title="Edit name and notes"
                  aria-label={`Edit ${favorite.hex} name and notes`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={onRemove}
                  className="fav-card__action fav-card__action--reveal fav-card__action--danger"
                  title="Remove from favorites"
                  aria-label={`Remove ${favorite.hex} from favorites`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
